import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent } from "react";
import { useParams } from "wouter";
import { api, ApiError } from "../api";
import { useCan } from "../auth/session";
import { pinAllowedFor, SignInActions, SignInCell, SignInPanel, SignInSettingsCard, useSignInSettings, type Panel, type SignInSummary } from "./SignInAdmin";
import { Card, DataTable, EmptyState, Field, InlineAlert, PageHeader, PageShell, RouteTabs, StatusChip, useToast, type StatusTone } from "../company-ui";
import { Tip } from "../help/Tip";
import { NeedsAttention } from "./NeedsAttention";

/**
 * Administration (ALN-24): Users, Roles and the Audit log, laid out as in the mockup's
 * Administration screen. Integrations shows Needs attention (ALN-26); its connections, Companies,
 * Settings and Data arrive with their own entries.
 */
const TABS = [["users", "Users"], ["roles", "Roles"], ["integrations", "Integrations"], ["settings", "Settings"], ["audit", "Audit log"]] as const;
type Tab = (typeof TABS)[number][0];
const SUBTITLE = "People, companies, connections and the defaults everything else uses.";

export function AdminPage() {
  const params = useParams<{ tab?: string }>();
  const manage = useCan("admin.manage");
  const tab: Tab = TABS.some(t => t[0] === params.tab) ? (params.tab as Tab) : "users";
  if (!manage) {
    return (
      <PageShell>
        <PageHeader title="Administration" subtitle={SUBTITLE} />
        <InlineAlert variant="info" title="Only owners and controllers can open Administration." />
      </PageShell>
    );
  }
  return (
    <PageShell>
      <PageHeader title="Administration" subtitle={SUBTITLE} />
      <RouteTabs label="Administration" current={tab} panelId="admin-panel" tabs={TABS.map(([id, label]) => ({ id, label, href: `/admin/${id}` }))} />
      <div id="admin-panel" role="tabpanel" aria-labelledby={`tab-${tab}`}>
        {tab === "users" ? <UsersTab /> : tab === "roles" ? <RolesTab /> : tab === "integrations" ? <NeedsAttention /> : tab === "settings" ? <SignInSettingsCard /> : <AuditTab />}
      </div>
    </PageShell>
  );
}

/* ------------------------------------------------------------ users */

interface Member { id: string; name: string; email: string; username?: string; contactEmail?: string | null; role: string; companyIds: string[] | null; status: "active" | "invited" | "deactivated"; lastSignedInAt: string | null; you: boolean; signIn?: SignInSummary }
interface Company { id: string; name: string }
const ASSIGNABLE = ["controller", "winemaker", "cellar", "estimator", "viewer"];
const STATUS_TONE: Record<Member["status"], StatusTone> = { active: "success", invited: "pending", deactivated: "neutral" };
const title = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "Never");

function UsersTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const users = useQuery({ queryKey: ["users"], queryFn: () => api<{ users: Member[] }>("/users") });
  const companies = useQuery({ queryKey: ["companies"], queryFn: () => api<{ companies: Company[] }>("/companies") });
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsent, setUnsent] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });
  const act = useMutation({
    mutationFn: ({ path, body, method }: { path: string; body?: object; method?: string }) => api(path, { method, body: body ?? {} }),
    onSuccess: (_d, v) => { setError(null); setConfirming(null); if (v.path.endsWith("/resend")) { setUnsent(null); toast("Invitation sent again."); } void refresh(); },
    onError: e => setError(message(e)),
  });
  const coName = (ids: string[] | null) => (ids ? ids.map(id => companies.data?.companies.find(c => c.id === id)?.name ?? "A company").join(", ") : "All companies");

  const settings = useSignInSettings();
  const [panel, setPanel] = useState<{ userId: string; panel: Panel } | null>(null);
  const [role, setRole] = useState("estimator");
  const [method, setMethod] = useState<"password" | "pin">("password");
  const pinOk = pinAllowedFor(role, settings.data);
  const min = settings.data?.settings.pinMinLength ?? 4;

  /**
   * Add person (sign-in-methods.md B.3): a password person whose username is their email gets the
   * ALN-24 invitation; anyone else is added with a temporary PIN or password, shown once.
   */
  const invite = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const co = String(f.get("companies"));
    const name = String(f.get("name") ?? "").trim();
    const username = String(f.get("username") ?? "").trim().toLowerCase();
    const email = String(f.get("email") ?? "").trim().toLowerCase();
    const how = method === "pin" && pinOk ? "pin" : "password";
    if (!name || !username) { setError("Add a name and a username."); return; }
    const companyIds = co === "all" ? null : [co];
    try {
      if (how === "password" && email && email === username) {
        const r = await api<{ sent: boolean }>("/users", { body: { name, email, role, companyIds } });
        setInviting(false); setError(null); void refresh();
        if (r.sent) { setUnsent(null); toast(`Invitation sent to ${email}.`); }
        else setUnsent(`${email} is invited, but the email didn't go out. Use Resend invitation to try again.`);
        return;
      }
      const r = await api<{ user: Member; temporaryPin?: string; temporaryPassword?: string }>("/users", { body: { name, username, email: email || null, role, companyIds, signInMethod: how } });
      setInviting(false); setError(null); await refresh();
      if (r.temporaryPin) setPanel({ userId: r.user.id, panel: { kind: "temp-pin", secret: r.temporaryPin } });
      else if (r.temporaryPassword) setPanel({ userId: r.user.id, panel: { kind: "temp-password", secret: r.temporaryPassword, switched: false } });
      else toast(`${name} is added. They sign in as they do at their other winery.`);
    } catch (err) { setError(message(err)); }
  };

  const list = users.data?.users ?? [];
  return (
    <Card
      title="Users"
      headingId="users-h"
      description={`${list.filter(u => u.status === "active").length} active, ${list.filter(u => u.status === "invited").length} invited. Survey answers stay anonymous whatever someone's role.`}
      actions={<button className="btn btn--primary" type="button" aria-expanded={inviting} onClick={() => { setInviting(!inviting); setError(null); }}>Invite someone</button>}
    >
      {inviting && (
        <form className="card__c fields" onSubmit={invite} aria-label="Invite someone">
          <Field label="Name"><input className="input" name="name" autoComplete="off" /></Field>
          <Field label="Username" hint="An email works as the username for people who have one."><input className="input" name="username" autoComplete="off" autoCapitalize="none" spellCheck={false} maxLength={320} /></Field>
          <Field label="Email (optional)" hint="For notices. Leave it blank if they have none."><input className="input" name="email" type="email" autoComplete="off" /></Field>
          <Field label="Role"><select className="select" name="role" value={role} onChange={e => setRole(e.target.value)}>{ASSIGNABLE.map(r => <option key={r} value={r}>{title(r)}</option>)}</select></Field>
          <Field label="Companies"><select className="select" name="companies" defaultValue="all"><option value="all">All companies</option>{companies.data?.companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <fieldset className="field signin-how">
            <legend className="field__label">How they sign in</legend>
            <label className="signin-opt"><input type="radio" name="method" checked={method === "password" || !pinOk} onChange={() => setMethod("password")} /><span>Password</span></label>
            <label className="signin-opt"><input type="radio" name="method" checked={method === "pin" && pinOk} disabled={!pinOk} aria-describedby={pinOk ? undefined : "add-pin-note"} onChange={() => setMethod("pin")} /><span>PIN ({min} to 6 numbers)</span></label>
            {!pinOk && <small className="field__hint" id="add-pin-note">{title(role)} can't sign in with a PIN at this winery. Owners can change that in Settings › Sign-in.</small>}
          </fieldset>
          <div className="field acct-acts"><button className="btn btn--primary" type="submit">Add person</button><button className="btn" type="button" onClick={() => { setInviting(false); setError(null); }}>Cancel</button></div>
        </form>
      )}
      {(error || unsent) && <div className="card__c">{error ? <InlineAlert variant="error" title={error} /> : <InlineAlert variant="warning" title={unsent!} />}</div>}
      <div className="card__c">
        <DataTable label="People" stack>
          <thead><tr><th scope="col">Person</th><th scope="col">Role<Tip term="Role" /></th><th scope="col">Companies<Tip term="Companies" /></th><th scope="col">Sign-in<Tip term="Sign-in" /></th><th scope="col">Status</th><th scope="col">Last active</th><th scope="col"><span className="sr">Actions</span></th></tr></thead>
          <tbody>
            {list.map(u => (
              <Fragment key={u.id}>
              <tr className={u.status === "deactivated" ? "is-off" : undefined}>
                <td>
                  <div className="stack">
                    <span><b>{u.name}</b>{u.you && <span className="muted"> (you)</span>}</span>
                    <span className="muted small">Username: {u.username ?? u.email}</span>
                    {u.contactEmail && u.contactEmail !== (u.username ?? u.email) && <span className="muted small">{u.contactEmail}</span>}
                    {!u.contactEmail && !(u.username ?? u.email).includes("@") && u.signIn && <span className="muted small">No email — signs in with a {u.signIn.method === "pin" ? "PIN" : "password"}</span>}
                  </div>
                </td>
                <td>
                  {u.you || u.role === "owner" ? <b>{title(u.role)}</b> : (
                    <>
                      <label className="sr" htmlFor={`ur-${u.id}`}>Role for {u.name}</label>
                      <select className="select select--sm" id={`ur-${u.id}`} value={u.role} disabled={u.status === "deactivated" || act.isPending}
                        onChange={e => act.mutate({ path: `/users/${u.id}`, method: "PATCH", body: { role: e.target.value } })}>
                        {ASSIGNABLE.map(r => <option key={r} value={r}>{title(r)}</option>)}
                      </select>
                    </>
                  )}
                </td>
                <td>{coName(u.companyIds)}</td>
                <td><SignInCell u={u} /></td>
                <td><StatusChip tone={STATUS_TONE[u.status]} quiet>{title(u.status)}</StatusChip></td>
                <td className="muted small">{when(u.lastSignedInAt)}</td>
                <td className="r">
                  <div className="row-acts">
                  <SignInActions u={u} settings={settings.data} onPanel={p => setPanel({ userId: u.id, panel: p })}
                    onUnlock={() => void api(`/admin/members/${u.id}/unlock`, { body: {} }).then(() => { toast(`${u.name} is unlocked.`); void refresh(); }, e => setError(message(e)))} />
                  {u.you || u.role === "owner" ? null : u.status === "invited" ? (
                    <button className="btn btn--ghost btn--sm" type="button" onClick={() => act.mutate({ path: `/users/${u.id}/resend` })}>Resend invitation</button>
                  ) : u.status === "active" ? (
                    confirming === u.id ? (
                      <form className="inline-confirm" aria-label={`Deactivate ${u.name}`} onSubmit={e => { e.preventDefault(); act.mutate({ path: `/users/${u.id}/deactivate`, body: { reason: new FormData(e.currentTarget).get("reason") || undefined } }); }}>
                        <label className="sr" htmlFor={`dr-${u.id}`}>Reason (optional)</label>
                        <input className="input input--sm" id={`dr-${u.id}`} name="reason" placeholder="Reason (optional)" />
                        <button className="btn btn--destructive btn--sm" type="submit">Deactivate</button>
                        <button className="btn btn--ghost btn--sm" type="button" onClick={() => setConfirming(null)}>Keep</button>
                      </form>
                    ) : <button className="btn btn--ghost btn--sm" type="button" onClick={() => setConfirming(u.id)}>Deactivate</button>
                  ) : (
                    <button className="btn btn--ghost btn--sm" type="button" onClick={() => act.mutate({ path: `/users/${u.id}/reactivate` })}>Reactivate</button>
                  )}
                  </div>
                </td>
              </tr>
              {panel?.userId === u.id && (
                <tr className="panelrow">
                  <td colSpan={7}>
                    <SignInPanel u={u} panel={panel.panel} min={min} setPanel={p => setPanel(p ? { userId: u.id, panel: p } : null)} onChanged={() => void refresh()} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </DataTable>
        {users.isPending && <p className="muted" aria-busy="true">Loading people…</p>}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------ roles */

function RolesTab() {
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api<{ roles: string[]; permissions: { id: string; label: string; roles: string[] }[] }>("/roles") });
  return (
    <Card title="Roles and permissions" headingId="roles-h" description="What each role can do. Owners and controllers manage who has which role.">
      <div className="card__c">
        <DataTable label="Permissions by role">
          <thead><tr><th scope="col">Permission</th>{roles.data?.roles.map(r => <th key={r} scope="col" className="c">{title(r)}</th>)}</tr></thead>
          <tbody>
            {roles.data?.permissions.map(p => (
              <tr key={p.id}>
                <td>{p.label}</td>
                {roles.data!.roles.map(r => (
                  <td key={r} className="c">{p.roles.includes(r) ? <span className="check" aria-label={`${title(r)} can`}>✓</span> : <span className="muted" aria-label={`${title(r)} cannot`}>—</span>}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </DataTable>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------ audit log */

interface Entry { id: string; at: string; actor: { id: string | null; name: string }; entity: string; entityId: string; action: string; before: unknown; after: unknown; reason: string | null }

/** One line a person can read: "Changed Sample Name's role from Viewer to Winemaker". */
export function describeEntry(e: Entry): string {
  const a = (e.after ?? {}) as { name?: string; role?: string; email?: string };
  const b = (e.before ?? {}) as { role?: string };
  if (e.entity === "membership") {
    const who = a.name ?? "someone";
    switch (e.action) {
      case "invite": return `Invited ${who} (${a.email}) as ${title(a.role ?? "")}`;
      case "change role": return `Changed ${who}'s role from ${title(b.role ?? "")} to ${title(a.role ?? "")}`;
      case "change companies": return `Changed which companies ${who} can see`;
      case "deactivate": return `Deactivated ${who}`;
      case "reactivate": return `Reactivated ${who}`;
      case "resend invitation": return `Sent ${who}'s invitation again`;
    }
  }
  if (e.entity === "sign_in") return describeSignIn(e.action, (e.after ?? {}) as SignInDetail);
  // ALN-128: sign-in changes made by an owner or controller, or by the person themself
  const signInChange: Record<string, string> = {
    "user.pin_set": "Set a PIN together with the person", "user.temporary_pin": "Gave a temporary PIN", "user.temporary_password": "Gave a temporary password",
    "user.unlocked": "Unlocked sign-in", "user.pin_chosen": "Chose their own PIN", "user.password_set": "Changed their password", "two_step.reset": "Turned off two-step sign-in",
    "two_step.backup_codes_renewed": "Made new backup codes", "settings.sign_in": "Changed the sign-in settings", "shared_device.marked": "Marked a computer as shared",
    "shared_device.unmarked": "Stopped treating a computer as shared", "support.sign_in_reset": "Forward Rise support reset a sign-in",
  };
  if (signInChange[e.action]) return signInChange[e.action]!;
  if (e.entity === "job_failure") {
    const d = (e.after ?? {}) as { what?: string };
    if (e.action === "job.dead_lettered") return `${d.what ?? "A background job"} didn't finish`;
    if (e.action === "job.retry") return "Tried a failed job again";
  }
  if (e.entity === "email_message" && e.action === "email.bounced") return "An email bounced; the address won't get more email";
  if (e.entity === "attachment") {
    const d = (e.after ?? e.before ?? {}) as { fileName?: string };
    if (e.action === "attachment.add") return `Attached ${d.fileName ?? "a file"}`;
    if (e.action === "attachment.remove") return `Removed ${(e.before as { fileName?: string } | null)?.fileName ?? "a file"}`;
    if (e.action === "attachment.download") return "Downloaded an attachment";
  }
  if (e.entity === "platform_access") return e.action === "open tenant" ? "Opened this winery for support" : `Viewed ${e.action.replace(/^GET \/api\//, "")}`;
  return `${title(e.action)} ${e.entity}`;
}

interface SignInDetail { method?: string; twoStep?: boolean; reason?: string; minutes?: number; failures?: number }

/** Sign-in events (ALN-23): the person is the actor, so these read from their side. */
function describeSignIn(action: string, d: SignInDetail): string {
  switch (action) {
    case "sign_in.succeeded": return `Signed in with ${d.method === "link" ? "a sign-in link" : d.method === "pin" ? "a PIN" : "a password"}${d.twoStep ? " and a two-step code" : ""}`;
    case "sign_in.pin_paused": return "PIN sign-in paused after 15 wrong PINs";
    case "two_step.backup_code_used": return "Signed in with a backup code";
    case "sign_in.failed":
      switch (d.reason) {
        case "wrong password": return "Sign-in failed: wrong password";
        case "locked": return "Sign-in refused: the account is locked";
        case "wrong code": return "Sign-in failed: wrong two-step code";
        case "too many codes": return "Sign-in ended after too many wrong two-step codes";
      }
      return "Sign-in failed";
    case "sign_in.locked": return `Account locked for ${d.minutes ?? 15} minutes after ${d.failures ?? 5} wrong passwords`;
    case "sign_in.link_requested": return "Asked for a sign-in link";
    case "two_step.enrolled": return "Set up two-step sign-in";
  }
  return action;
}

function AuditTab() {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const audit = useInfiniteQuery({
    queryKey: ["audit", filters],
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams(Object.entries({ ...filters, before: pageParam }).filter(([, v]) => v));
      return api<{ entries: Entry[]; next: string | null }>(`/audit?${q}`);
    },
    getNextPageParam: last => last.next ?? undefined,
  });
  const apply = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFilters(Object.fromEntries([...new FormData(e.currentTarget)].map(([k, v]) => [k, String(v)]).filter(([, v]) => v)));
  };
  const entries = audit.data?.pages.flatMap(p => p.entries) ?? [];
  return (
    <Card title="Audit log" headingId="audit-h" description="Every change to money, rates, users and surveys, newest first. Entries can't be edited or removed.">
      <form className="card__c fields" onSubmit={apply} aria-label="Filter the audit log">
        <Field label="Kind"><select className="select" name="entity" defaultValue=""><option value="">Everything</option><option value="membership">People and roles</option><option value="sign_in">Sign-ins</option><option value="platform_access">Avero support access</option></select></Field>
        <Field label="Action starts with"><input className="input" name="action" placeholder="deactivate" /></Field>
        <Field label="From"><input className="input" name="from" type="date" /></Field>
        <Field label="To"><input className="input" name="to" type="date" /></Field>
        <div className="field"><button className="btn" type="submit">Filter</button></div>
      </form>
      {audit.isError && <div className="card__c"><InlineAlert variant="error" title={message(audit.error)} /></div>}
      <div className="card__c stackgap">
        <DataTable label="Audit entries" stack>
          <thead><tr><th scope="col">When</th><th scope="col">Who</th><th scope="col">What</th><th scope="col">Why</th></tr></thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id}>
                <td className="muted small nowrap">{when(e.at)}</td>
                <td className="nowrap">{e.actor.name}</td>
                <td>{describeEntry(e)}</td>
                <td className="muted">{e.reason ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
        {!audit.isPending && entries.length === 0 && <EmptyState title="Nothing matches these filters" />}
        {audit.hasNextPage && <div><button className="btn" type="button" disabled={audit.isFetchingNextPage} onClick={() => void audit.fetchNextPage()}>Show older entries</button></div>}
      </div>
    </Card>
  );
}
